
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Zap, Calendar, Euro } from "lucide-react";
import { toast } from "sonner";
import { createActivityPrice, updateActivityPrice, ActivityPrice } from "@/hooks/useFinanceData";

interface Activity {
  id: string;
  name: string;
}

interface PriceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activities: Activity[];
  editingPrice?: ActivityPrice | null;
  onSuccess: () => void;
  // Para creación dirigida
  preselectedActivity?: string;
  preselectedBillingType?: 'per_session' | 'monthly';
}

export const PriceFormDialog = ({ 
  open, 
  onOpenChange, 
  activities, 
  editingPrice, 
  onSuccess,
  preselectedActivity,
  preselectedBillingType
}: PriceFormDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    activity_id: '',
    price_amount: '',
    billing_type_target: 'per_session' as 'per_session' | 'monthly',
    effective_from: new Date().toISOString().split('T')[0],
    effective_until: '',
  });

  // Actualizar el formulario cuando cambien las props
  useEffect(() => {
    setFormData({
      activity_id: preselectedActivity || editingPrice?.activity_id || '',
      price_amount: editingPrice?.price_amount?.toString() || '',
      billing_type_target: preselectedBillingType || editingPrice?.billing_type_target || 'per_session',
      effective_from: editingPrice?.effective_from || new Date().toISOString().split('T')[0],
      effective_until: editingPrice?.effective_until || '',
    });
  }, [preselectedActivity, preselectedBillingType, editingPrice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form data:', formData);
    console.log('Available activities:', activities);
    
    if (!formData.activity_id || !formData.price_amount) {
      console.log('Form validation failed - missing required fields');
      if (!formData.activity_id) {
        toast.error("Por favor selecciona una actividad");
      }
      if (!formData.price_amount) {
        toast.error("Por favor ingresa un precio");
      }
      return;
    }

    try {
      setLoading(true);
      
      if (editingPrice) {
        await updateActivityPrice(editingPrice.id, {
          price_amount: parseFloat(formData.price_amount),
          effective_until: formData.effective_until || undefined,
        });
        toast.success("Precio actualizado correctamente");
      } else {
        const newPrice = {
          activity_id: formData.activity_id,
          price_amount: parseFloat(formData.price_amount),
          billing_type_target: formData.billing_type_target,
          effective_from: formData.effective_from,
          effective_until: formData.effective_until || undefined,
        };
        await createActivityPrice(newPrice);
        toast.success("Precio creado correctamente");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error("Error al guardar el precio");
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedActivity = activities.find(a => a.id === formData.activity_id);
  const suggestedPrice = formData.billing_type_target === 'monthly' ? '60.00' : '8.00';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="w-5 h-5 text-primary" />
            {editingPrice ? 'Editar Precio' : 'Nuevo Precio'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="activity">Actividad</Label>
            <Select
              value={formData.activity_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, activity_id: value }))}
              disabled={!!editingPrice || !!preselectedActivity}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar actividad" />
              </SelectTrigger>
              <SelectContent>
                {activities.map((activity) => (
                  <SelectItem key={activity.id} value={activity.id}>
                    {activity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="billing_type_target">Tipo de Precio</Label>
            <Select
              value={formData.billing_type_target}
              onValueChange={(value: 'per_session' | 'monthly') => 
                setFormData(prev => ({ ...prev, billing_type_target: value }))
              }
              disabled={!!editingPrice && !preselectedBillingType}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="per_session">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-600" />
                    <span>Por Día</span>
                    <Badge variant="outline" className="text-xs">€/sesión</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="monthly">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <span>Mensual</span>
                    <Badge variant="outline" className="text-xs">€/mes</Badge>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price_amount">
              Precio ({formData.billing_type_target === 'per_session' ? '€/sesión' : '€/mes'})
            </Label>
            <Input
              id="price_amount"
              type="number"
              step="0.01"
              min="0"
              value={formData.price_amount}
              onChange={(e) => setFormData(prev => ({ ...prev, price_amount: e.target.value }))}
              placeholder={suggestedPrice}
              required
            />
            <p className="text-xs text-muted-foreground">
              {formData.billing_type_target === 'per_session' 
                ? '💡 Precio por cada sesión a la que asista el estudiante'
                : '💡 Cuota fija mensual independiente de las asistencias'
              }
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="effective_from">Efectivo desde</Label>
            <Input
              id="effective_from"
              type="date"
              value={formData.effective_from}
              onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="effective_until">Efectivo hasta (opcional)</Label>
            <Input
              id="effective_until"
              type="date"
              value={formData.effective_until}
              onChange={(e) => setFormData(prev => ({ ...prev, effective_until: e.target.value }))}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {editingPrice ? 'Actualizar' : 'Crear'} Precio
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
